import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar } from "lucide-react";

import icon1 from "../img/linea/icon1.png";
import icon2 from "../img/linea/icon2.png";
import icon3 from "../img/linea/icon3.png";
import icon4 from "../img/linea/icon4.png";
import icon5 from "../img/linea/icon5.png";
import icon6 from "../img/linea/icon6.png";
import icon7 from "../img/linea/icon7.png";
import icon8 from "../img/linea/icon8.png";
import icon9 from "../img/linea/icon9.png";
import icon10 from "../img/linea/icon10.png";

const timelineData = [
  {
    fecha: "1971 - 1975",
    titulo: "Los Orígenes",
    contenido:
      "Fundación marista e inspiración en modelos europeos para democratizar la educación superior.",
    imageUrl: icon1,
  },
  {
    fecha: "Sep. 1976",
    titulo: "Nace la Universidad Abierta",
    contenido:
      'Aprobación oficial e implementación pionera del modelo de "educación personalizada".',
    imageUrl: icon2,
  },
  {
    fecha: "Oct. 1976",
    titulo: "Primeros Pasos",
    contenido:
      "Apertura con 19 centros y 1.273 alumnos. Enfoque inicial en la profesionalización del magisterio.",
    imageUrl: icon3,
  },
  {
    fecha: "1981",
    titulo: "Primer Egresado",
    contenido:
      "Se gradúa el primer profesional de la modalidad. La institución se expande a nivel iberoamericano.",
    imageUrl: icon4,
  },
  {
    fecha: "1997",
    titulo: "Misioneros Identes",
    contenido:
      "Nueva administración asume el liderazgo, detonando una etapa de crecimiento exponencial.",
    imageUrl: icon5,
  },
  {
    fecha: "1998 - 2000",
    titulo: "Expansión Global",
    contenido:
      "La matrícula se duplica. Se abren sedes internacionales en Nueva York, Madrid y Roma.",
    imageUrl: icon6,
  },
  {
    fecha: "2009 - 2017",
    titulo: "Salto Tecnológico",
    contenido:
      "Transición ecológica: evolución de libros impresos a entornos virtuales (EVA) y entrega de tablets.",
    imageUrl: icon7,
  },
  {
    fecha: "2020",
    titulo: "Resiliencia Digital",
    contenido:
      "Estudio ininterrumpido en pandemia para 35.000 alumnos. Innovación con materiales offline.",
    imageUrl: icon8,
  },
  {
    fecha: "2022",
    titulo: "Laboratorios XR y UTPL TEC",
    contenido:
      "Nacen las carreras tecnológicas cortas y se integran aulas de Realidad Virtual y Aumentada.",
    imageUrl: icon9,
  },
  {
    fecha: "2024",
    titulo: "Transformación digital e innovación educativa",
    contenido:
      " La transición hacia la Modalidad en Línea y el uso innovador de inteligencia artificial generativa posicionaron a la UTPL como líder en educación digital, reconocimiento respaldado por el primer lugar en Ecuador y el puesto 14 a nivel mundial en el OLR 2024 de Times Higher Education.",
    imageUrl: icon10,
  },
];

export default function ModalTimeline() {
  const [selectedItem, setSelectedItem] = useState(null);

  return (
    <section className="bg-white py-24 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto overflow-hidden">
      {/* Encabezado Principal */}
      <div className="text-center">
        <h2>Nuestra historia</h2>
        <p>
          Un recorrido por los hitos más importantes que han definido nuestra
          trayectoria académica y tecnológica.
        </p>
      </div>

      {/* Contenedor Principal con Ondas de Fondo */}
      <div className="relative py-12">
        {/* --- LÍNEAS ONDULADAS DE FONDO (SVG) --- */}
        <div className="hidden md:block absolute inset-0 pointer-events-none z-0 overflow-hidden">
          <svg
            className="w-full h-full"
            viewBox="0 0 1200 200 "
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Línea Amarilla (Onda Inferior) */}
            <path
              d="M -50 112 C 150 162, 250 22, 450 102 C 650 192, 750 42, 950 122 C 1050 162, 1150 92, 1250 92"
              stroke="#FAAE19"
              strokeWidth="4"
              strokeLinecap="round"
            />
            {/* Línea Azul (Onda Superior) */}
            <path
              d="M -50 100 C 150 150, 250 10, 450 90 C 650 180, 750 30, 950 110 C 1050 150, 1150 80, 1250 80"
              stroke="#004270"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Grid adaptable con los 10 ítems visibles a la vez */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-3 relative z-10">
          {timelineData.map((item, index) => {
            const isTop = index % 2 === 0;

            return (
              <motion.div
                key={index}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedItem(item)}
                className={`cursor-pointer bg-white/95 rounded-xl p-3 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between items-center text-center group ${
                  isTop ? "md:-translate-y-6" : "md:translate-y-6"
                }`}
              >
                {/* Imagen del Ítem */}
                <div>
                  <img
                    src={item.imageUrl}
                    alt={item.titulo}
                    className="h-full group-hover:scale-110"
                  />
                </div>

                <div className="relative -top-10">
                  <span className="text-xs font-extrabold text-[#004270] bg-gray-100 p-2 rounded-full">
                    {item.fecha}
                  </span>
                </div>

                <div className="relative -top-6">
                  <p className="text-4xl leading-snug">
                    <b>{item.titulo}</b>
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* MODAL DETALLADO CON FRAMER MOTION */}
      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Fondo Oscuro / Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
              className="absolute inset-0 bg-blue-950/60 backdrop-blur-sm"
            />

            {/* Ventana Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-lg w-full p-6 sm:p-8 z-10 overflow-hidden"
            >
              {/* Botón de Cierre */}
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-blue-950 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors duration-200"
                aria-label="Cerrar modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Insignia de Fecha */}
              <div className="inline-flex items-center gap-1.5 bg-amber-50 text-[#febe10] font-bold text-sm px-3 py-1 rounded-full mb-4 border border-amber-200/60">
                <Calendar className="w-4 h-4" />
                <span>{selectedItem.fecha}</span>
              </div>

              {/* Imagen Grande en Modal */}
              <div className="w-full h-48 sm:h-56 bg-gray-50 rounded-xl overflow-hidden mb-6 border border-gray-100 flex items-center justify-center">
                <img
                  src={selectedItem.imageUrl}
                  alt={selectedItem.titulo}
                  className="w-full h-full object-contain p-2"
                />
              </div>

              {/* Contenido */}
              <div>
                <h2>{selectedItem.titulo}</h2>
                <p>{selectedItem.contenido}</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
